import { HealthShareApiError } from '../backend/httpTransport'
import type { BackendApi } from '../backend/client'
import type { MembershipRoleRow, MembershipRow, OrganizationRow } from '../types/supabase'

export interface InviteSpouseParams {
  email: string
  name: string
  /** The existing spouse dependent row to link, if the spouse is already a dependent. */
  dependentId?: string
}

/**
 * Invites a spouse to co-administer a membership. The caller must already be an
 * admin of the membership; a Solo subscriber is refused with
 * `HealthShareApiError.code === 'family_plan_required'` -- see
 * {@link isFamilyPlanRequiredError}.
 */
export function inviteSpouse(api: BackendApi, membershipId: string, params: InviteSpouseParams) {
  return api.inviteSpouse(membershipId, {
    email: params.email,
    name: params.name,
    dependent_id: params.dependentId,
  })
}

/**
 * Whether an `inviteSpouse` failure was the backend's family-plan gate (the
 * backstop for the up-front UI gate -- reachable when the persisted Redux
 * subscription tier is stale against the current DB state). Callers show the
 * upgrade guidance instead of the generic "check the email" message.
 */
export function isFamilyPlanRequiredError(err: unknown): boolean {
  return err instanceof HealthShareApiError && err.code === 'family_plan_required'
}

/**
 * Startup hydration: the caller's membership, role, profile, settings, and
 * dependents, in one request. Replaces ~8 sequential PostgREST reads -- see
 * docs/plans/native-app-sdk-seams.md, Cleanup B.
 */
export function getMyMembershipContext(api: BackendApi) {
  return api.getMyMembershipContext()
}

export interface SaveMembershipParams {
  /**
   * Update this pre-created membership instead of creating one -- the
   * free-trial invite path, where the caller is already its admin.
   */
  membershipId?: string
  memberNumber?: string | null
  sharingMinistryType?: string | null
  iuaAmount?: number | null
  smiPlan?: string | null
}

/**
 * Onboarding's membership save: creates the membership, grants the caller the
 * admin role, and links their organization, in one server-side transaction.
 *
 * Replaces the client-orchestrated sequence in setMembershipDetails.tsx, which
 * issued those writes one at a time with no transaction -- a failure between
 * the membership insert and the role insert left a membership its own creator
 * could not administer. See docs/plans/native-app-sdk-seams.md, Stage 2 Step 4.
 */
export async function saveMyMembership(
  api: BackendApi,
  params: SaveMembershipParams,
): Promise<SavedMembership> {
  const response = await api.saveMyMembership({
    membership_id: params.membershipId,
    member_number: params.memberNumber,
    sharing_ministry_type: params.sharingMinistryType,
    iua_amount: params.iuaAmount,
    smi_plan: params.smiPlan,
  })

  return response as unknown as SavedMembership
}

/**
 * The three rows an onboarding save produces.
 *
 * The cast above is the same one `loadReduxStateFromServer` makes against
 * `GET /memberships/mine`: `membership_roles.id` is a bigint, which Eloquent
 * serializes as a string and openapi.yaml therefore types as one, while the
 * generated `MembershipRoleRow` (from the database schema) types it as a
 * number. Resolving that properly means changing what the endpoints send,
 * which would be a breaking change for every existing client -- so it stays a
 * documented seam rather than an undocumented one.
 */
export interface SavedMembership {
  membership: MembershipRow
  role: MembershipRoleRow
  organization: OrganizationRow | null
}

/** The member-writable membership columns outside the onboarding save. */
export interface MembershipUpdate {
  member_number?: string | null
  alice_health_data_consent_at?: string | null
  alice_health_data_consent_source?: string | null
  alice_health_data_consent_message_id?: string | null
  alice_health_data_consent_revoked_at?: string | null
}

/**
 * Updates the member number or the Alice health-data consent flags. The caller
 * must be an admin of the membership, which is what the dropped
 * "Can change membership number if admin" RLS policy required.
 */
export async function updateMyMembership(
  api: BackendApi,
  membershipId: string,
  update: MembershipUpdate,
): Promise<MembershipRow> {
  const response = await api.updateMyMembership(membershipId, update)
  return response.membership as MembershipRow
}

/**
 * Whether a membership save or update was refused because the member number is
 * already taken. The uniqueness is on (member_number, sharing_ministry_type),
 * so two ministries may issue the same number.
 */
export function isMemberNumberTakenError(err: unknown): boolean {
  return err instanceof HealthShareApiError && err.code === 'member_number_taken'
}
